import React from "react";
import styled, { css } from "styled-components";
import { Link, withRouter } from "react-router-dom";
import isEqual from "lodash/isEqual";
import ActivityTimestamp from "./ActivityTimestamp";
import Avatar from "../Avatar";
import { userDisplayName, urlRoot } from "../../utils/helpers";
import * as colors from "../../settings/colors";
import {
  COMMENT_AVATAR_SIZE as AVATAR_SIZE,
  COMMENT_AVATAR_PADDING as AVATAR_PADDING
} from "../../settings/metrics";
import showdown from "showdown";
import CommentForm from "./CommentForm";

const markdownConverter = new showdown.Converter();

const CommentDetails = styled.div`
  position: relative;
  flex: 1;
  margin-left: ${AVATAR_PADDING}px;

  &:before,
  &:after {
    position: absolute;
    top: ${(AVATAR_SIZE - 14) / 2}px;
    right: 100%;
    left: -15px;
    display: block;
    width: 0;
    height: 0;
    pointer-events: none;
    content: " ";
    border-color: transparent;
    border-style: solid solid outset;
  }

  &:before {
    border-width: 8px;
    border-right-color: ${colors.DIVIDER};
  }

  &:after {
    margin-top: 1px;
    margin-left: 2px;
    border-width: 7px;
    border-right-color: ${colors.SIDEBAR_BG};
  }
`;

const CommentBody = styled.div`
  border: 1px solid ${colors.DIVIDER};
  border-top: 0;
  padding: 15px;
  font-size: 14px;
  line-height: 19px;
  border-radius: 0 0 4px 4px;

  *:first-child {
    margin-top: 0;
  }

  *:last-child {
    margin-bottom: 0;
  }
`;

const CommentHeader = styled.div`
  display: flex;
  font-size: 14px;
  line-height: 1;
  color: ${colors.H2_TEXT};
  background: ${colors.SIDEBAR_BG};
  border: 1px solid ${colors.DIVIDER};
  padding: ${(AVATAR_SIZE - 14 - 2) / 2}px 15px;
  border-radius: 4px 4px 0 0;

  /* TODO: Something? I can't remember */

  b {
    color: ${colors.H1_TEXT};
    font-weight: 600;
  }
`;

const StyledLink = styled(Link)`
  color: ${colors.H1_TEXT};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CommentActions = styled.div`
  margin-left: auto;
`;

const Action = styled.a`
  cursor: pointer;
  margin-left: 8px;

  ${props =>
    props.delete &&
    css`
      &:hover {
        color: ${colors.RED};
      }
    `};
`;

const ServiceLink = ({ service, match }) => {
  if (!service) {
    return null;
  }
  const link = `${urlRoot(match.url)}/jobs/${service.job.id}/services/${
    service.id
  }`;

  return <StyledLink to={link}>{service.serviceType}</StyledLink>;
};

// prettier-ignore
const CommentWrapper = styled.div`
  display: flex;
  margin: 16px 0;

  ${props => !props.public && css`
    ${CommentDetails}:before {
      border-right-color: #D9CBAE;
    }

    ${CommentDetails}:after {
      border-right-color: #F5E9D1;
    }
  
    ${CommentHeader} {
      color: #74674B;
      border-color: #D9CBAE;
      background: #F5E9D1;

      b {
        color: #53462B;
      }
    }

    ${StyledLink} {
      color: #53462B;
    }
  
    ${CommentBody} {
      color: #53462B;
      border-color: #D9CBAE;
      background: #FFFCF5;
    }
  `}
`;

class Comment extends React.Component {
  state = {
    isEditing: false
  };

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.isEditing !== nextState.isEditing ||
      this.props.showService !== nextProps.showService ||
      !isEqual(this.props.activity, nextProps.activity)
    );
  }

  handleEdit = () => {
    this.setState({ isEditing: true });
  };

  handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      return this.props.deleteComment(this.props.activity);
    }
  };

  handleSubmit = values => {
    if (this.props.updateComment) {
      return this.props.updateComment(values).then(() => {
        this.setState({ isEditing: false });
      });
    }

    return Promise.reject();
  };

  render() {
    const {
      match,
      activity,
      showService = true,
      updateComment,
      deleteComment
    } = this.props;
    const bodyMarkup = { __html: markdownConverter.makeHtml(activity.body) };

    if (this.state.isEditing) {
      return (
        <CommentForm
          onSubmit={this.handleSubmit}
          comment={activity}
          serviceId={match.params.serviceId}
        />
      );
    }

    return (
      <CommentWrapper public={activity.public}>
        <Avatar
          email={activity.actor.email}
          style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
        />
        <CommentDetails>
          <CommentHeader>
            <div>
              <b>{userDisplayName(activity.actor.name)}</b> commented{" "}
              {showService &&
                activity.service && (
                  <span>
                    on <ServiceLink service={activity.service} match={match} />{" "}
                  </span>
                )}
              <ActivityTimestamp>{activity.createdAt}</ActivityTimestamp>
            </div>
            <CommentActions>
              {updateComment && <Action onClick={this.handleEdit}>Edit</Action>}
              {deleteComment && (
                <Action delete onClick={this.handleDelete}>
                  Delete
                </Action>
              )}
            </CommentActions>
          </CommentHeader>
          <CommentBody dangerouslySetInnerHTML={bodyMarkup} />
        </CommentDetails>
      </CommentWrapper>
    );
  }
}

export default withRouter(Comment);



// WEBPACK FOOTER //
// ./src/components/activities/Comment.js